import { Chip } from "@mui/material";
import { ChipWrap } from "components/ChipWrap";
import { useNotification } from "providers/Notification";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { AddNewMaskFormat } from "./AddMaskFormat";
import { FormDrawer } from "components/FormDrawer";
import { ListPageHeader } from "components/ListPageHeader";
import { ConfirmDialog } from "components/ConfirmDialog";
import DataTable, { Column } from "components/Table";
import { useResourceList } from "hooks/useResourceList";
import { useDeleteDialog } from "hooks/useDeleteDialog";
import { useFormDrawer } from "hooks/useFormDrawer";
import { formatDateTime, fromUnix } from "helpers/dateFormat";
import { useRole } from "hooks/useRole";

interface ObfuscationsState {
    id: string
    name: string
}

interface MaskFormatListStateItem {
    id: string
    name: string
    type: Number
    internal: Number
    maskCharacter: string
    stopCharacter: string
    charactersToIgnore: string
    numberOfCharacters?: Number
    keepCharacters: Number
    reverseOrder: Number
    updatedAt: Date
    createdAt: Date
    obfuscations?: ObfuscationsState[]
}

type columnsType = "id" | "internal" | "name" | "usedby" | "mask_character" | "updated_at";

const defaultColumns: Column<columnsType>[] = [
    { columnId: "name", width: "25%", direction: "asc", sortOrder: 2 },
    { columnId: "id", width: "20%" },
    { columnId: "internal", width: "10%", direction: "asc", sortOrder: 1 },
    { columnId: "usedby", width: "25%" },
    { columnId: "mask_character", width: "10%" },
    { columnId: "updated_at", width: "10%" },
];

function toMaskFormat(x: any): MaskFormatListStateItem {
    return {
        id: x.id,
        name: x.name,
        type: x.type,
        internal: x.internal,
        maskCharacter: x.maskCharacter,
        stopCharacter: x.stopCharacter,
        charactersToIgnore: x.charactersToIgnore,
        numberOfCharacters: x.numberOfCharacters,
        keepCharacters: x.keepCharacters,
        reverseOrder: x.reverseOrder,
        updatedAt: fromUnix(x.updatedAt),
        createdAt: fromUnix(x.createdAt),
        obfuscations: x.obfuscations && x.obfuscations.map((item: any) => ({
            id: item.id,
            name: item.name,
        })),
    };
}

export default function MaskFormats() {
    const notification = useNotification();
    const { canEditPolicy } = useRole();

    const list = useResourceList<MaskFormatListStateItem, columnsType>({
        url: "/api/maskformats",
        columnsKey: "maskformats_v3",
        defaultColumns,
        mapItem: toMaskFormat,
        paginated: true,
        sorted: true,
        searchable: true,
    });

    const drawer = useFormDrawer();
    const del = useDeleteDialog({
        url: "/api/maskformats",
        label: "mask format",
        onDeleted: list.reload,
    });

    return <>
        <ListPageHeader
            title="Mask Formats"
            search={list.search}
            onSearchChange={list.setSearch}
            searchAllFields={list.searchAllFields}
            onSearchAllFieldsChange={list.setSearchAllFields}
            actionId="new-mask-format-button"
            actionLabel="Add New Mask Format"
            // NS-753
            onAction={canEditPolicy ? drawer.add : undefined}
        />

        <DataTable
            {...list.tableProps}
            columns={{
                id: {
                    displayName: "Id",
                    sortable: true,
                    render: (x) => x.id,
                },
                internal: {
                    displayName: "Internal",
                    sortable: true,
                    render: (x) => x.internal ? "YES" : "NO",
                },
                name: {
                    displayName: "Name",
                    sortable: true,
                    render: (x) => x.name,
                },
                usedby: {
                    displayName: "Used By Obfuscation(s)",
                    render: (x) => <ChipWrap>
                        {x.obfuscations && x.obfuscations.map(y => <Chip label={y.name.trim()} variant="outlined" size="small" color="primary" />)}
                    </ChipWrap>,
                },
                mask_character: {
                    displayName: "Mask Char.",
                    render: (x) => x.maskCharacter,
                },
                updated_at: {
                    displayName: "Last Update",
                    sortable: true,
                    render: (x) => formatDateTime(x.updatedAt),
                },
            }}
            rowOptionsMenu={[
                {
                    icon: <EditIcon className="edit-button" fontSize="small" />,
                    text: canEditPolicy ? "Edit" : "View",
                    isVisible: (x) => !x.internal,
                    onClick: (x) => drawer.edit(x.id),
                },
                {
                    icon: <DeleteIcon className="delete-button" fontSize="small" />,
                    text: "Delete",
                    isVisible: (x) => !x.internal && canEditPolicy,
                    onClick: (x) => {
                        if (x.obfuscations && x.obfuscations.length > 0) {
                            return notification.Display({
                                type: "error",
                                title: "Validation Error",
                                message: `Cannot delete ${x.name} because it is in use by ${x.obfuscations.length === 1 ? "an Obfuscation" : "some Obfuscations"} (${x.obfuscations.map(i => i.name).join(", ")})`,
                            });
                        }

                        del.ask(x.name, x.id);
                    },
                },
            ]}
        />

        <ConfirmDialog {...del.dialogProps} title="Delete Mask Format" />

        <FormDrawer {...drawer.drawerProps}>
            <AddNewMaskFormat
                maskFormatId={drawer.editingId}
                onExit={drawer.close}
                onSave={() => { list.reload(); drawer.close(); }}
            />
        </FormDrawer>
    </>;
}
